import { observer } from 'mobx-react-lite'
import { type FC } from 'react'
import classnames from 'classnames'
import Highlighter from 'react-highlight-words'
import {
  Avatar,
  DropdownCard,
  EnumDropdownItemVariant,
  type IDropdownItem,
  InputCheckbox,
  type InputCheckboxProps,
  type ITooltipProps,
  Typography,
} from 'shared/ui'
import { type IUser } from 'entities/Users'
import styles from './styles.module.scss'

type IUserDropdownCardProps = {
  item: IUser
  withoutOnlineStatus?: boolean
  InputCheckboxProps?: InputCheckboxProps
  onChange?: (item: IUser) => void
  highlighter?: string
  ariaLabel?: string
  tooltipProps?: ITooltipProps
  disabled?: boolean
}

type IUserDropdownCardContentProps = {
  item: IUser
  highlighter?: string
  isCheckbox?: boolean
  withoutOnlineStatus?: boolean
}

export const UserCardContent: FC<IUserDropdownCardContentProps> = ({
  item,
  highlighter,
  isCheckbox,
  withoutOnlineStatus,
}) => {
  return (
    <div
      className={classnames(styles.wrap, {
        [styles['wrap--checkbox']]: isCheckbox,
      })}
    >
      <div className={styles.icon}>
        <Avatar
          size={16}
          info={item?.avatarInfo}
          online={withoutOnlineStatus ? undefined : !item.isAway}
        />
      </div>
      <Typography variant={'body-md-regular'} ariaLabel={'name'} className={styles.title}>
        {highlighter ? (
          <Highlighter
            highlightClassName={classnames(styles.highlighter, styles['highlighter--label'])}
            className={styles.name}
            searchWords={highlighter.split(' ')}
            autoEscape={true}
            textToHighlight={item.name || ''}
          />
        ) : (
          item.name
        )}
      </Typography>
    </div>
  )
}

export const UserDropdownCard: FC<IUserDropdownCardProps> = observer(
  ({
    item,
    withoutOnlineStatus,
    onChange,
    InputCheckboxProps,
    highlighter,
    ariaLabel,
    tooltipProps,
    disabled,
  }) => {
    const renderContent = (isCheckbox: boolean) => {
      return (
        <UserCardContent
          item={item}
          highlighter={highlighter}
          isCheckbox={isCheckbox}
          withoutOnlineStatus={withoutOnlineStatus}
        />
      )
    }
    const dropdown: IDropdownItem = {
      id: item.id,
      label: item.name,
      variant: EnumDropdownItemVariant.Custom,
      renderOption: () => {
        return renderContent(false)
      },
      parentTooltipProps: tooltipProps,
      disabled: disabled,
    }

    if (InputCheckboxProps) {
      return (
        <InputCheckbox
          className={styles.checkbox}
          {...InputCheckboxProps}
          customLabel={renderContent(true)}
          tooltipProps={tooltipProps}
          disabled={disabled}
        />
      )
    }

    return (
      <DropdownCard
        item={dropdown}
        ariaLabel={ariaLabel}
        onChange={() => {
          if (onChange) onChange(item)
        }}
      />
    )
  }
)
